import { Box } from '@mui/material';
import { Vertical } from '@repo/supergraph/marketplace/constants';
import { StateAbbreviation } from '@repo/supergraph/marketplace/types';
import { Roundup } from '@repo/supergraph/roundup/base/types';
import { getFilterSlugForPage } from '@repo/ui/roundups/components/OffersFilter/utils';
import { snakeCase } from 'lodash';
import { useRef } from 'react';
import { DesktopFilters } from '../OffersFilter/DesktopFilterBar';
import { DesktopFiltersWithGeo } from '../OffersFilter/DesktopFilterBar/DesktopFiltersWithGeo';
import { MobileFilters, MobileFiltersWithGeo } from '../OffersFilter/MobileFilters';
import { OffersFilterStoreProvider } from '../OffersFilter/store';
import type { FilterEnabledSlugs } from '../OffersFilter/store/types';

type Props = {
  children: React.ReactNode;
  sortedRows: Roundup['summaryTableSection']['rows'];
  slug: string;
  initialLocation?: StateAbbreviation;
  vertical: Vertical;
  isPaid?: boolean;
  // This prop is used to determine if the HYSA redesign is active. If the treatment is the winner we can remove this prop and add the HYSA slug to the filter config instead.
  isHysaRedesignActive?: boolean;
  // When true, skips rendering the filter UI and OffersFilterStoreProvider entirely (returns children directly).
  // A parent OffersFilterStoreProvider — supplied by FiltersInHeaderProvider in packages/ui/src/roundups/BankBonuses —
  // MUST wrap this component, otherwise filter state and hooks will be missing.
  hideFilterUI?: boolean;
};

export const ProductsFiltersWrapper = ({
  children,
  initialLocation,
  isPaid = false,
  isHysaRedesignActive = false,
  hideFilterUI = false,
  sortedRows,
  slug,
  vertical,
}: Props) => {
  const filteredProductsRef = useRef<HTMLDivElement>(null);
  const snakeCasedSlug = snakeCase(slug);

  const filterSlug = isHysaRedesignActive
    ? (slug as FilterEnabledSlugs)
    : getFilterSlugForPage(slug, isPaid, Boolean(initialLocation));
  const analyticsProps = {
    actionSection: `${vertical}_${snakeCasedSlug}_filter`,
    pageIdentifier: snakeCasedSlug,
  };

  if (filterSlug) {
    if (hideFilterUI) {
      return children;
    }

    return (
      <OffersFilterStoreProvider offers={sortedRows} slug={filterSlug}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 2,
            maxWidth: { xs: '100%', md: '82%' },
            marginX: 'auto',
          }}
        >
          {initialLocation ? (
            <DesktopFiltersWithGeo
              mainSectionRef={filteredProductsRef}
              slug={filterSlug}
              initialLocation={initialLocation}
              analyticsProps={analyticsProps}
            />
          ) : (
            <DesktopFilters
              mainSectionRef={filteredProductsRef}
              slug={filterSlug}
              analyticsProps={analyticsProps}
            />
          )}
        </Box>
        {initialLocation ? (
          <MobileFiltersWithGeo
            slug={filterSlug}
            initialLocation={initialLocation}
            analyticsProps={analyticsProps}
          />
        ) : (
          <MobileFilters
            slug={filterSlug}
            initialLocation={initialLocation}
            analyticsProps={analyticsProps}
          />
        )}
        <Box ref={filteredProductsRef}>{children}</Box>
      </OffersFilterStoreProvider>
    );
  }

  return children;
};
